import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react'
import { flowHubService, FlowChange, FlowParticipant, FlowChangeData } from '@services/flowHub.service'
import { tokenStorage } from '@services/tokenStorage'
import { useAuthStore } from '@store/authStore'
import { useWorkflowStore, WorkflowNode, WorkflowEdge } from '@store/workflowStore'

// Types for events that consumers can subscribe to
export interface UserJoinedEvent {
  flowId: string
  userId: string
  userName: string
  timestamp: string
}

export interface UserLeftEvent {
  flowId: string
  userId: string
  userName: string
  timestamp: string
}

export interface SyncRequestEvent {
  flowId: string
  requesterId: string
  requesterName: string
}

// Context value interface
export interface AdventureCollaborationContextValue {
  // Connection state
  isConnected: boolean
  isConnecting: boolean
  participants: FlowParticipant[]
  error: string | null
  adventureId: string | null

  // Flow/workflow-specific operations
  sendNodeAdd: (node: WorkflowNode) => Promise<void>
  sendNodeMove: (nodeId: string, position: { x: number; y: number }) => Promise<void>
  sendNodeUpdate: (nodeId: string, data: Record<string, unknown>) => Promise<void>
  sendNodeDelete: (nodeId: string) => Promise<void>
  sendEdgeAdd: (edge: WorkflowEdge) => Promise<void>
  sendEdgeUpdate: (edgeId: string, data: Record<string, unknown>) => Promise<void>
  sendEdgeDelete: (edgeId: string) => Promise<void>
  sendWorkflowReplace: (nodes: WorkflowNode[], edges: WorkflowEdge[]) => Promise<void>

  // Adventure field updates
  sendAdventureFieldUpdate: (field: string, value: unknown) => Promise<void>

  // Generic change sending (for future adventure-level changes)
  sendChange: (change: FlowChangeData) => Promise<void>

  // Utility
  refreshParticipants: () => Promise<void>
  requestSync: () => Promise<void>

  // Event subscription (for components that need to react to events)
  subscribeToRemoteChanges: (handler: (change: FlowChange) => void) => () => void
  subscribeToUserJoined: (handler: (data: UserJoinedEvent) => void) => () => void
  subscribeToUserLeft: (handler: (data: UserLeftEvent) => void) => () => void
  subscribeToSyncRequest: (handler: (data: SyncRequestEvent) => void) => () => void
}

const AdventureCollaborationContext = createContext<AdventureCollaborationContextValue | undefined>(undefined)

interface AdventureCollaborationProviderProps {
  adventureId: string | null
  children: ReactNode
}

export function AdventureCollaborationProvider({
  adventureId,
  children,
}: AdventureCollaborationProviderProps) {
  const [isConnected, setIsConnected] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [participants, setParticipants] = useState<FlowParticipant[]>([])
  const [error, setError] = useState<string | null>(null)

  const { isAuthenticated, user } = useAuthStore()

  // Subscriber refs for event handling
  const remoteChangeHandlersRef = useRef<Set<(change: FlowChange) => void>>(new Set())
  const userJoinedHandlersRef = useRef<Set<(data: UserJoinedEvent) => void>>(new Set())
  const userLeftHandlersRef = useRef<Set<(data: UserLeftEvent) => void>>(new Set())
  const syncRequestHandlersRef = useRef<Set<(data: SyncRequestEvent) => void>>(new Set())

  // Connect to FlowHub and join adventure
  useEffect(() => {
    if (!adventureId || !isAuthenticated) {
      return
    }

    const token = tokenStorage.getAccessToken()
    if (!token) {
      console.warn('[AdventureCollaboration] No auth token available')
      return
    }

    let mounted = true

    const connect = async () => {
      setIsConnecting(true)
      setError(null)

      try {
        // Set up callbacks before connecting
        flowHubService.setCallbacks({
          onUserJoined: (data) => {
            console.log('[AdventureCollaboration] User joined:', data.userName)
            // Refresh participants list when someone joins
            flowHubService.getParticipants()
            // Notify subscribers
            userJoinedHandlersRef.current.forEach(handler => handler(data))
          },
          onUserLeft: (data) => {
            console.log('[AdventureCollaboration] User left:', data.userName)
            if (mounted) {
              setParticipants(prev => prev.filter(p => p.userId !== data.userId))
            }
            // Notify subscribers
            userLeftHandlersRef.current.forEach(handler => handler(data))
          },
          onFlowChange: (change) => {
            console.log('[AdventureCollaboration] Received change:', change.changeData.type)
            // Notify all subscribers
            remoteChangeHandlersRef.current.forEach(handler => handler(change))
          },
          onParticipants: (data) => {
            console.log('[AdventureCollaboration] Participants updated:', data.participants.length)
            if (mounted) {
              setParticipants(data.participants)
            }
          },
          onError: (message) => {
            console.warn('[AdventureCollaboration] Error (suppressed):', message)
          },
          onReconnecting: () => {
            console.log('[AdventureCollaboration] Reconnecting...')
            if (mounted) {
              setIsConnected(false)
            }
          },
          onReconnected: () => {
            console.log('[AdventureCollaboration] Reconnected')
            if (mounted) {
              setIsConnected(true)
            }
          },
          onDisconnected: () => {
            console.log('[AdventureCollaboration] Disconnected')
            if (mounted) {
              setIsConnected(false)
            }
          },
          onSyncRequest: (data) => {
            console.log('[AdventureCollaboration] Sync requested by:', data.requesterName)
            // Only respond if we're the "oldest" participant (first in the list after excluding requester)
            // This prevents multiple users from sending sync data simultaneously
            const currentParticipants = flowHubService.getCachedParticipants()
            const otherParticipants = currentParticipants.filter(p => p.userId !== data.requesterId)

            if (otherParticipants.length > 0 && otherParticipants[0].userId === user?.userId) {
              console.log('[AdventureCollaboration] We are the oldest participant, notifying sync subscribers')
              syncRequestHandlersRef.current.forEach(handler => handler(data))
            } else {
              console.log('[AdventureCollaboration] Another participant will handle sync')
            }
          },
        })

        // Connect and join flow
        await flowHubService.connect(token)

        if (!mounted) return

        await flowHubService.joinFlow(adventureId)

        if (!mounted) return

        // Get initial participants list
        await flowHubService.getParticipants()

        // Check if there are other participants - if so, request sync
        const currentParticipants = flowHubService.getCachedParticipants()
        const otherParticipants = currentParticipants.filter(p => p.userId !== user?.userId)
        if (otherParticipants.length > 0) {
          console.log('[AdventureCollaboration] Other participants present, requesting sync')
          await flowHubService.requestSync()
        }

        if (mounted) {
          setIsConnected(true)
          setIsConnecting(false)
        }
      } catch (err) {
        console.warn('[AdventureCollaboration] Connection error (suppressed):', err)
        if (mounted) {
          setIsConnecting(false)
        }
      }
    }

    connect()

    return () => {
      mounted = false
      flowHubService.leaveFlow()
      flowHubService.clearCallbacks()
    }
  }, [adventureId, isAuthenticated, user?.userId])

  // Helper to silently handle SignalR send errors — connection issues are transient
  // and should not interrupt the user experience
  const safeSend = useCallback(async (fn: () => Promise<void>) => {
    try {
      await fn()
    } catch (err) {
      console.warn('[AdventureCollaboration] Send failed (suppressed):', err)
    }
  }, [])

  // Send node add
  const sendNodeAdd = useCallback(async (node: WorkflowNode) => {
    await safeSend(() => flowHubService.sendChange({
      type: 'node_add',
      node: {
        id: node.id,
        type: node.type || 'default',
        position: node.position,
        data: node.data as Record<string, unknown>,
      },
    }))
  }, [safeSend])

  // Send node move
  const sendNodeMove = useCallback(async (nodeId: string, position: { x: number; y: number }) => {
    await safeSend(() => flowHubService.sendChange({
      type: 'node_move',
      nodeId,
      position,
    }))
  }, [safeSend])

  // Send node update
  const sendNodeUpdate = useCallback(async (nodeId: string, data: Record<string, unknown>) => {
    await safeSend(() => flowHubService.sendChange({
      type: 'node_update',
      nodeId,
      data,
    }))
  }, [safeSend])

  // Send node delete
  const sendNodeDelete = useCallback(async (nodeId: string) => {
    await safeSend(() => flowHubService.sendChange({
      type: 'node_delete',
      nodeId,
    }))
  }, [safeSend])

  // Send edge add
  const sendEdgeAdd = useCallback(async (edge: WorkflowEdge) => {
    await safeSend(() => flowHubService.sendChange({
      type: 'edge_add',
      edge: {
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle || undefined,
        targetHandle: edge.targetHandle || undefined,
        data: edge.data as Record<string, unknown> | undefined,
      },
    }))
  }, [safeSend])

  // Send edge update
  const sendEdgeUpdate = useCallback(async (edgeId: string, data: Record<string, unknown>) => {
    await safeSend(() => flowHubService.sendChange({
      type: 'edge_update',
      edgeId,
      data,
    }))
  }, [safeSend])

  // Send edge delete
  const sendEdgeDelete = useCallback(async (edgeId: string) => {
    await safeSend(() => flowHubService.sendChange({
      type: 'edge_delete',
      edgeId,
    }))
  }, [safeSend])

  // Send workflow replace
  const sendWorkflowReplace = useCallback(async (nodes: WorkflowNode[], edges: WorkflowEdge[]) => {
    await safeSend(() => {
      const changeSequence = useWorkflowStore.getState().changeSequence
      return flowHubService.sendChange({
        type: 'workflow_replace',
        nodes: nodes.map(n => ({
          id: n.id,
          type: n.type || 'default',
          position: n.position,
          data: n.data as Record<string, unknown>,
        })),
        edges: edges.map(e => ({
          id: e.id,
          source: e.source,
          target: e.target,
          sourceHandle: e.sourceHandle || undefined,
          targetHandle: e.targetHandle || undefined,
          data: e.data as Record<string, unknown> | undefined,
        })),
        changeSequence,
      })
    })
  }, [safeSend])

  // Send adventure field update
  const sendAdventureFieldUpdate = useCallback(async (field: string, value: unknown) => {
    await safeSend(() => flowHubService.sendChange({
      type: 'adventure_field_update',
      field,
      value,
    }))
  }, [safeSend])

  // Generic change sending
  const sendChange = useCallback(async (change: FlowChangeData) => {
    await safeSend(() => flowHubService.sendChange(change))
  }, [safeSend])

  // Refresh participants
  const refreshParticipants = useCallback(async () => {
    await safeSend(() => flowHubService.getParticipants())
  }, [safeSend])

  // Request sync
  const requestSync = useCallback(async () => {
    await safeSend(() => flowHubService.requestSync())
  }, [safeSend])

  // Event subscription functions
  const subscribeToRemoteChanges = useCallback((handler: (change: FlowChange) => void) => {
    remoteChangeHandlersRef.current.add(handler)
    return () => {
      remoteChangeHandlersRef.current.delete(handler)
    }
  }, [])

  const subscribeToUserJoined = useCallback((handler: (data: UserJoinedEvent) => void) => {
    userJoinedHandlersRef.current.add(handler)
    return () => {
      userJoinedHandlersRef.current.delete(handler)
    }
  }, [])

  const subscribeToUserLeft = useCallback((handler: (data: UserLeftEvent) => void) => {
    userLeftHandlersRef.current.add(handler)
    return () => {
      userLeftHandlersRef.current.delete(handler)
    }
  }, [])

  const subscribeToSyncRequest = useCallback((handler: (data: SyncRequestEvent) => void) => {
    syncRequestHandlersRef.current.add(handler)
    return () => {
      syncRequestHandlersRef.current.delete(handler)
    }
  }, [])

  const value: AdventureCollaborationContextValue = {
    isConnected,
    isConnecting,
    participants,
    error,
    adventureId,
    sendNodeAdd,
    sendNodeMove,
    sendNodeUpdate,
    sendNodeDelete,
    sendEdgeAdd,
    sendEdgeUpdate,
    sendEdgeDelete,
    sendWorkflowReplace,
    sendAdventureFieldUpdate,
    sendChange,
    refreshParticipants,
    requestSync,
    subscribeToRemoteChanges,
    subscribeToUserJoined,
    subscribeToUserLeft,
    subscribeToSyncRequest,
  }

  return (
    <AdventureCollaborationContext.Provider value={value}>
      {children}
    </AdventureCollaborationContext.Provider>
  )
}

export function useAdventureCollaboration() {
  const context = useContext(AdventureCollaborationContext)
  if (context === undefined) {
    throw new Error('useAdventureCollaboration must be used within an AdventureCollaborationProvider')
  }
  return context
}

// Optional hook for components that don't need the full context
// Returns null if not within a provider (useful for standalone components)
export function useAdventureCollaborationOptional() {
  return useContext(AdventureCollaborationContext) ?? null
}
